import React, { useEffect, useState } from 'react';
import {
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	Paper,
	Box,
	Typography,
	Link,
	Button,
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
	List,
	ListItem,
	ListItemText,
	TextField,
	Grid,
} from '@mui/material';

import { useDispatch, useSelector } from 'react-redux';
import { obtenerPedidos } from 'scripts/obtenerPedidos';
import { metodosPago, pedidoEstados } from 'utils/enums';
import { toast } from 'react-toastify';
import CustomDialog from 'helpers/Dialog';
import { actualizarEstadoPedido } from 'scripts/actualizarEstadoPedido';
import { obtenerTelefonoWhatsAppUruguay } from 'utils/userUtils';
import { formatFechaEntrega } from 'utils/dateUtils';

const PedidosManagement = () => {
	const dispatch = useDispatch();
	const [pedido, setPedido] = useState(null);
	const [dialogOpen, setDialogOpen] = useState(false);
	const [detalleDialogOpen, setDetalleDialogOpen] = useState(false);
	const [pedidoSeleccionado, setPedidoSeleccionado] = useState(null);
	const [filtroImporteMin, setFiltroImporteMin] = useState('');
	const [filtroImporteMax, setFiltroImporteMax] = useState('');
	const [filtroFechaEntrega, setFiltroFechaEntrega] = useState('');
	const [filtroEstadoPago, setFiltroEstadoPago] = useState('');

	// Fetch de datos (pedidos)
	const fetchPedidos = async () => {
		obtenerPedidos(dispatch, sessionStorage.getItem('token')).catch((e) => {
			toast.error(e.message);
		});
	};

	useEffect(() => {
		try {
			fetchPedidos();
		} catch (e) {
			console.error(e);
		}
	}, [dispatch]);

	const getEstado = (order) => {
		if (order.estado === 3 && order.retiro_local) {
			return 'LISTO_PARA_RETIRAR';
		} else {
			return pedidoEstados[order.estado];
		}
	};

	const obtenerFechaEntregaISO = (fechaEntrega) => {
		if (!fechaEntrega) {
			return '';
		}

		return String(fechaEntrega).split('T')[0];
	};

	const orders = useSelector((state) => state.pedidos.pedidos || []);
	const estadosPagoOptions = Array.from(
		new Set(
			orders
				.map((order) => String(order?.estado_pago || 'SIN_PAGO').trim())
				.filter((estadoPago) => estadoPago.length > 0),
		),
	);

	const handleLimpiarFiltros = () => {
		setFiltroImporteMin('');
		setFiltroImporteMax('');
		setFiltroFechaEntrega('');
		setFiltroEstadoPago('');
	};

	const calcularTotalPedido = (order) => {
		const totalGuardado = Number(order?.total);
		if (!Number.isNaN(totalGuardado)) {
			return totalGuardado;
		}
		if (!order?.detalles || order.detalles.length === 0) {
			return Number(order?.total || 0);
		}
		return order.detalles.reduce((acc, detalle) => acc + Number(detalle?.subtotal || 0), 0);
	};

	const obtenerMontoPagado = (order) => {
		if (order?.monto_pagado === null || order?.monto_pagado === undefined) {
			return null;
		}
		const monto = Number(order.monto_pagado);
		return Number.isNaN(monto) ? null : monto;
	};

	const handleOpenDialog = (id) => {
		setPedido(id);
		setDialogOpen(true);
	};

	const handleCloseDialog = () => {
		setPedido(null);
		setDialogOpen(false);
	};

	const handleConfirmDialog = async () => {
		actualizarEstadoPedido(pedido, 4, sessionStorage.getItem('token'))
			.then((data) => {
				toast.success(data.message);
				fetchPedidos();
			})
			.catch((e) => {
				toast.error(e.error);
			});
		setPedido(null);
		setDialogOpen(false);
	};

	const handleOpenDetalle = (pedido) => {
		setPedidoSeleccionado(pedido);
		setDetalleDialogOpen(true);
	};

	const handleCloseDetalle = () => {
		setPedidoSeleccionado(null);
		setDetalleDialogOpen(false);
	};

	const renderTelefonoCliente = (telefono) => {
		const telefonoWhatsapp = obtenerTelefonoWhatsAppUruguay(telefono);
		if (!telefonoWhatsapp) {
			return (
				<Typography variant="body2" sx={{ color: 'text.secondary' }}>
					{telefono}
				</Typography>
			);
		}

		return (
			<Link
				href={`https://wa.me/${telefonoWhatsapp}`}
				target="_blank"
				rel="noopener noreferrer"
				underline="hover"
				sx={{ fontSize: '0.875rem' }}
			>
				{telefono}
			</Link>
		);
	};

	const esPedidoAdmin = (order) =>
		Boolean(order?.es_pedido_admin) ||
		String(order?.cliente_nombre || '').trim().toLowerCase() === 'cliente generico';

	const obtenerTextoCliente = (order) => {
		if (esPedidoAdmin(order)) {
			const notasPedido = String(order?.notas || '').trim();
			return notasPedido || 'Sin notas';
		}
		return order?.cliente_nombre;
	};

	const filteredOrders = orders.filter((order) => {
		const totalPedido = calcularTotalPedido(order);
		const fechaEntrega = obtenerFechaEntregaISO(order.fecha_entrega);
		const estadoPago = String(order?.estado_pago || 'SIN_PAGO').trim();

		const matchesImporteMin = filtroImporteMin === '' || totalPedido >= Number(filtroImporteMin);
		const matchesImporteMax = filtroImporteMax === '' || totalPedido <= Number(filtroImporteMax);
		const matchesFechaEntrega = !filtroFechaEntrega || fechaEntrega === filtroFechaEntrega;
		const matchesEstadoPago = !filtroEstadoPago || estadoPago === filtroEstadoPago;

		return matchesImporteMin && matchesImporteMax && matchesFechaEntrega && matchesEstadoPago;
	});

	return (
		<div style={{ marginTop: '20px' }}>
			<Box
				sx={{
					marginBottom: 2,
					paddingBottom: 2,
					borderBottom: '2px solid #ccc',
				}}
			>
				<Grid container spacing={2} alignItems="center">
					<Grid item xs={12} sm={6} md={2}>
						<TextField
							label="Importe mínimo"
							type="number"
							variant="outlined"
							fullWidth
							value={filtroImporteMin}
							onChange={(e) => setFiltroImporteMin(e.target.value)}
						/>
					</Grid>
					<Grid item xs={12} sm={6} md={2}>
						<TextField
							label="Importe máximo"
							type="number"
							variant="outlined"
							fullWidth
							value={filtroImporteMax}
							onChange={(e) => setFiltroImporteMax(e.target.value)}
						/>
					</Grid>
					<Grid item xs={12} sm={6} md={2}>
						<TextField
							label="Fecha de entrega"
							type="date"
							variant="outlined"
							fullWidth
							InputLabelProps={{ shrink: true }}
							value={filtroFechaEntrega}
							onChange={(e) => setFiltroFechaEntrega(e.target.value)}
						/>
					</Grid>
					<Grid item xs={12} sm={6} md={3}>
						<TextField
							label="Estado de pago"
							variant="outlined"
							fullWidth
							select
							InputLabelProps={{ shrink: true }}
							SelectProps={{
								native: true,
							}}
							value={filtroEstadoPago}
							onChange={(e) => setFiltroEstadoPago(e.target.value)}
						>
							<option value="">Todos</option>
							{estadosPagoOptions.map((estadoPago) => (
								<option key={estadoPago} value={estadoPago}>
									{estadoPago}
								</option>
							))}
						</TextField>
					</Grid>
					<Grid item xs={12} sm={6} md={3}>
						<Button
							variant="outlined"
							color="secondary"
							fullWidth
							sx={{ height: 56 }}
							onClick={handleLimpiarFiltros}
						>
							Limpiar filtros
						</Button>
					</Grid>
				</Grid>
			</Box>
			{/* Tabla de pedidos */}
			<TableContainer component={Paper} sx={{ maxHeight: 600, overflow: 'auto' }}>
				<Table>
					<TableHead sx={{ position: 'sticky', top: 0, backgroundColor: '#FFFFFF' }}>
						<TableRow>
							<TableCell>Numero de pedido</TableCell>
							<TableCell>Estado</TableCell>
							<TableCell>Pago</TableCell>
							<TableCell>Total</TableCell>
							<TableCell>Monto pagado</TableCell>
							<TableCell>Notas del Cadete</TableCell>
							<TableCell sx={{ whiteSpace: 'nowrap' }}>Fecha de entrega</TableCell>
							<TableCell>Cliente</TableCell>
							<TableCell>Acciones</TableCell>
						</TableRow>
					</TableHead>
					<TableBody>
						{filteredOrders.length > 0 ? (
							filteredOrders.map((order) => (
								<TableRow key={order.id}>
									<TableCell>{order.id}</TableCell>
									<TableCell>{getEstado(order)}</TableCell>
									<TableCell>
										{metodosPago[order.metodo_pago - 1].nombre + '\n' + order.estado_pago}
									</TableCell>
									<TableCell>${calcularTotalPedido(order).toFixed(2)}</TableCell>
									<TableCell>
										{obtenerMontoPagado(order) !== null ?
											`$${obtenerMontoPagado(order).toFixed(2)}`
										:	'Pendiente'}
									</TableCell>
									<TableCell>{order.notas_cadete}</TableCell>
									<TableCell sx={{ whiteSpace: 'nowrap' }}>
										{formatFechaEntrega(order.fecha_entrega)}
									</TableCell>
									<TableCell>
										<Box sx={{ display: 'flex', flexDirection: 'column' }}>
											<Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
												{obtenerTextoCliente(order)}
											</Typography>
											{!esPedidoAdmin(order) &&
												order.cliente_telefono &&
												renderTelefonoCliente(order.cliente_telefono)}
										</Box>
									</TableCell>
									<TableCell>
										<Button
											variant="outlined"
											color="info"
											onClick={() => handleOpenDetalle(order)}
											sx={{ marginRight: 1 }}
										>
											Ver detalle de pedido
										</Button>
										{order.retiro_local &&
											order.estado < 4 &&
											order.estado_pago !== 'PENDIENTE' && (
												<Button
													variant="outlined"
													color="primary"
													onClick={() => handleOpenDialog(order.id)}
												>
													Marcar como Entregado
												</Button>
											)}
									</TableCell>
								</TableRow>
							))
						) : (
							<TableRow>
								<TableCell colSpan={9} style={{ textAlign: 'center' }}>
									No hay pedidos disponibles.
								</TableCell>
							</TableRow>
						)}
					</TableBody>
				</Table>
			</TableContainer>
			<CustomDialog
				open={dialogOpen}
				title="Confirmar Acción"
				content="¿Está seguro de que desea marcar como ENTREGADO este pedido?"
				onClose={handleCloseDialog}
				onConfirm={handleConfirmDialog}
			/>

			{/* Modal de Detalles del Pedido */}
			<Dialog open={detalleDialogOpen} onClose={handleCloseDetalle} maxWidth="sm" fullWidth>
				<DialogTitle>Detalle del Pedido #{pedidoSeleccionado?.id}</DialogTitle>
				<DialogContent>
					{pedidoSeleccionado?.detalles && pedidoSeleccionado.detalles.length > 0 ? (
						<List>
							{pedidoSeleccionado.detalles
								.filter((detalle) => detalle.descripcion !== 'Envío')
								.map((detalle, index) => (
									<ListItem key={index} divider>
										<ListItemText
											primary={detalle.descripcion}
											secondary={`Cantidad: ${detalle.cantidad} | Subtotal: $${detalle.subtotal}`}
										/>
									</ListItem>
								))}
						</List>
					) : (
						<p style={{ textAlign: 'center', padding: '20px', color: '#666' }}>
							No hay detalles disponibles para este pedido.
						</p>
					)}
				</DialogContent>
				<DialogActions>
					<Button onClick={handleCloseDetalle} color="primary">
						Cerrar
					</Button>
				</DialogActions>
			</Dialog>
		</div>
	);
};

export default PedidosManagement;
